import { formatISO } from 'date-fns';
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { TransactionType } from '@/globalTypes';
import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  adjustmentSummary_stats,
  getAdjustmentsNgrSummaryRecord,
  getAdjustmentsSummaryRecord,
  getBonusAdjustmentsSummaryRecord,
  getCombinedAdjustmentsSummaryRecord,
} from '@/utils/stats-with-currency';
import {
  PlayerAdjustmentsQuery,
  PlayerAdjustmentsQueryVariables,
} from './__generated__/usePlayerAdjustments';
import { queryParams } from './queryParams';

const PLAYER_TRANSACTION_QUERY = gql`
  query PlayerAdjustments(
    $playerId: ID!
    $id: String
    $rawId: String
    $from: OffsetDateTime
    $to: OffsetDateTime
    $now: OffsetDateTime!
    $paymentStatus: [PaymentStatus!]
    $transactionTypes: [TransactionType!]
    $paymentMethod: String
    $minAmount: PositiveBigDecimal
    $maxAmount: PositiveBigDecimal
    $adjustmentType: AdjustmentType
    $adjustmentCategory: AdjustmentCategory
    $orderBy: TransactionSearchOrder
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    player(playerId: $playerId) {
      id
      allTimeStats: stats(
        granularity: All
        from: "2019-12-01T00:00:00+00:00"
        to: $now
      ) {
        rows {
          ...AdjustmentSummary_stats
        }
      }
      transactionsV2(
        id: $id
        rawId: $rawId
        from: $from
        to: $to
        paymentStatus: $paymentStatus
        transactionTypes: $transactionTypes
        paymentMethod: $paymentMethod
        minAmount: $minAmount
        maxAmount: $maxAmount
        adjustmentType: $adjustmentType
        adjustmentCategory: $adjustmentCategory
        orderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        edges {
          node {
            processedAt
            amount
            currency
            transactionType
            rawTransactionId
            adjustmentType {
              category
            }
            comment
            initiator {
              ... on PlayerInitiator {
                player {
                  firstName
                  lastName
                  email
                }
              }
              ... on AgentInitiator {
                agent {
                  email
                  firstName
                  lastName
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
  ${adjustmentSummary_stats}
`;

export default function usePlayerTransactions(playerId: string) {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const now = useMemo(() => new Date(), []);

  const [{ data, fetching }, refresh] = useQuery<
    PlayerAdjustmentsQuery,
    PlayerAdjustmentsQueryVariables
  >({
    query: PLAYER_TRANSACTION_QUERY,
    variables: {
      playerId,
      ...mapVariables(query),
      now: formatISO(now),
      transactionTypes: [
        TransactionType.AdjustmentDeposit,
        TransactionType.AdjustmentWithdrawal,
      ],
    },
  });

  const adjustmentSummaries = {
    ngr: getAdjustmentsNgrSummaryRecord(data?.player.allTimeStats?.rows),
    combinedAdjustments: getCombinedAdjustmentsSummaryRecord(
      data?.player.allTimeStats?.rows,
    ),
    adjustments: getAdjustmentsSummaryRecord(data?.player.allTimeStats?.rows),
    bonusAdjustments: getBonusAdjustmentsSummaryRecord(
      data?.player.allTimeStats?.rows,
    ),
  };

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, data?.player.transactionsV2.pageInfo),
    adjustments: data?.player.transactionsV2?.edges?.map((edge) => edge?.node),
    adjustmentSummaries,
  };
}
